import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Tag, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { SEASON_THEMES } from "@/lib/seasons";

export const PROMO_STORAGE_KEY = "hb_promo_code";
const REMINDERS_KEY = "hb_season_reminders";

// If the visitor asked to be reminded about this season, say hi once and forget the reminder.
function announceReminder(season) {
  const list = JSON.parse(localStorage.getItem(REMINDERS_KEY) || "[]");
  if (!list.includes(season.slug)) return;
  localStorage.setItem(REMINDERS_KEY, JSON.stringify(list.filter((s) => s !== season.slug)));
  toast(`${season.emoji} The ${season.label} collection is open — you asked us to remind you!`, { duration: 7000 });
}

function Confetti({ theme }) {
  return (
    <div className="absolute inset-0 pointer-events-none select-none" aria-hidden="true">
      {theme.confetti.map((c, i) => (
        <span key={`${c.e}-${c.x}-${c.y}`} className={`absolute text-2xl sm:text-3xl hb-float ${c.x > 20 && c.x < 80 ? "hidden sm:block" : ""}`} style={{ left: `${c.x}%`, top: `${c.y}%`, animationDelay: `${i * 0.7}s`, opacity: 0.85 }}>{c.e}</span>
      ))}
    </div>
  );
}

function PromoChip({ promo, theme }) {
  const [copied, setCopied] = useState(false);
  const promoText = promo.kind === "percent" ? `${promo.value}% off` : `$${Number(promo.value).toFixed(2)} off`;
  const grabCode = async () => {
    try { await navigator.clipboard.writeText(promo.code); } catch (err) { console.warn("Clipboard blocked — code still saved for checkout", err); }
    localStorage.setItem(PROMO_STORAGE_KEY, promo.code);
    setCopied(true);
    toast.success(`${promo.code} copied — it'll apply itself at checkout 🎃`);
    setTimeout(() => setCopied(false), 2500);
  };
  return (
    <button type="button" onClick={grabCode} data-testid="seasonal-banner-promo"
      className="mt-4 inline-flex items-center gap-2 rounded-2xl border-2 border-dashed px-4 py-2.5 text-sm font-semibold hover:scale-[1.02] transition-transform"
      style={{ borderColor: theme.cta, background: "rgba(255,255,255,0.08)", color: theme.fg }}>
      <Tag className="w-4 h-4" style={{ color: theme.cta }} />
      <span>Use code <span className="font-display font-black tracking-widest" style={{ color: theme.cta }} data-testid="seasonal-banner-promo-code">{promo.code}</span> for {promoText}{promo.min_subtotal > 0 ? ` on orders over $${Number(promo.min_subtotal).toFixed(0)}` : ""}</span>
      <span className="ml-1 text-xs opacity-80 inline-flex items-center gap-1">{copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : "Tap to copy"}</span>
    </button>
  );
}

function ProductThumbs({ products }) {
  if (!products.length) return null;
  return (
    <div className="flex justify-center lg:justify-end gap-3" data-testid="seasonal-banner-products">
      {products.map((p, i) => (
        <Link key={p.id} to={`/product/${p.slug}`} className="block w-28 sm:w-36 rounded-3xl overflow-hidden bg-white soft-shadow hover:-translate-y-1 transition-transform" style={{ transform: `rotate(${(i - 1) * 4}deg)` }} data-testid={`seasonal-banner-product-${p.slug}`}>
          <img src={p.images?.[0]} alt={p.name} className="w-full aspect-square object-cover" loading="lazy" />
        </Link>
      ))}
    </div>
  );
}

export default function SeasonalBanner() {
  const [season, setSeason] = useState(null);
  useEffect(() => {
    api.get("/seasons/active").then(({ data }) => {
      setSeason(data.active ? data : null);
      if (data.active) announceReminder(data);
    }).catch((err) => console.warn("Seasonal banner unavailable", err));
  }, []);
  if (!season) return null;

  const t = SEASON_THEMES[season.slug] || SEASON_THEMES.default;
  const daysLeft = Math.max(0, Math.ceil((new Date(season.ends_on) - new Date()) / 86400000));

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-6 pb-2" data-testid="seasonal-banner" data-season={season.slug}>
      <div className="relative overflow-hidden rounded-[2rem] soft-shadow" style={{ background: t.bg, color: t.fg }}>
        <Confetti theme={t} />
        <div className="relative grid lg:grid-cols-[1.4fr_1fr] gap-6 items-center p-7 sm:p-10">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-display font-bold tracking-wide uppercase" style={{ background: t.pill, color: t.pillFg }} data-testid="seasonal-banner-pill">
              {season.emoji} {season.label} collection{daysLeft > 0 ? ` · ${daysLeft} day${daysLeft === 1 ? "" : "s"} left` : ""}
            </span>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl leading-tight mt-3" style={{ color: t.fg }} data-testid="seasonal-banner-title">{t.headline}</h2>
            <p className="mt-3 text-base sm:text-lg max-w-xl" style={{ color: t.fgSoft }}>{t.tagline}</p>
            {season.promo && <PromoChip promo={season.promo} theme={t} />}
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link to={`/shop?seasonal=${season.slug}`} className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-display font-bold text-sm hover:scale-105 transition-transform" style={{ background: t.cta, color: t.ctaFg }} data-testid="seasonal-banner-cta">
                {t.ctaLabel} <ArrowRight className="w-4 h-4" />
              </Link>
              <span className="text-sm font-semibold" style={{ color: t.fgSoft }} data-testid="seasonal-banner-count">{season.product_count} {season.product_count === 1 ? "piece" : "pieces"} in the collection</span>
            </div>
          </div>
          <ProductThumbs products={(season.products || []).slice(0, 3)} />
        </div>
      </div>
    </section>
  );
}
